/**
 * StudentLearningPanel — 학생 학습 화면(매스컨셉/문제 중심)
 *
 * KyTestPage.js 의 본체를 추출한 공유 컴포넌트.
 * - variant='page'    : 단독 페이지(/student/:id/kytest). 자체 BypassLayout 렌더.
 * - variant='embedded': StudentsHub 우측 패널. BypassLayout 미렌더(hub 셸이 담당), 헤더 우측은 닫기 버튼.
 *
 * 상단: AI Tracker(AI/Engine/RawData, 매스컨셉) / 하단: 교육과정 관리(Stamp)
 * studentId 전환 시에는 hub 에서 key={studentId} 로 remount 하여 stale 상태를 방지한다.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { BypassLayout, Button } from '../common';
import { AITrackerContent } from '../AITrackerContent';
import { ArrowLeft, X, Sparkles, Loader2 } from 'lucide-react';
import ExtractionConfigModal from '../KyTestPage/ExtractionConfigModal';
import ProblemPreviewPanel from './ProblemPreviewPanel';
import HubSyllabusBadgeList from './HubSyllabusBadgeList';
import { clearHubSyllabusCaches } from './hubSyllabusCache';
import { useDesktopExpanded } from '../../hooks/useDesktopExpanded';

/** 차트 아래 칸 보기(매스컨셉 표 / 강의) — 이 브라우저에 마지막 보기를 기억한다(학생과 무관, 설계 FE-20260914 §7) */
const TABLE_VIEW_KEY = 'students-hub:learning-table-view';
const TABLE_VIEWS = [['concept', '매스컨셉'], ['syllabus', '강의']];
const readTableView = () => {
  try { return window.localStorage.getItem(TABLE_VIEW_KEY) === 'syllabus' ? 'syllabus' : 'concept'; } catch (e) { return 'concept'; }
};

/** 보기 전환 단추 — 강의 바꾸기 판의 범위 탭(SyllabusSwitcher tabCls)과 같은 얼굴. 두 보기의 머리 띠 같은 자리에 선다 */
function TableViewToggle({ value, onChange }) {
  return (
    <div className="flex shrink-0 overflow-hidden rounded-full bg-gray-100" role="group" aria-label="차트 아래 칸 보기">
      {TABLE_VIEWS.map(([k, label]) => (
        <button
          key={k}
          type="button"
          aria-pressed={value === k}
          onClick={() => onChange(k)}
          className={`whitespace-nowrap px-3 py-1 text-xs transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary ${value === k ? 'bg-primary font-medium text-white' : 'text-gray-500 hover:bg-gray-200'}`}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

function StudentLearningPanel({ studentId, variant = 'embedded', onClose, onLearningChanged }) {
  const navigate = useNavigate();
  const { getAccessToken } = useAuth();
  const { isMobileView: hookIsMobileView } = useDesktopExpanded();
  const isPage = variant === 'page';

  // 기본 상태 - 학생 정보는 genetics API에서 가져옴
  const [student, setStudent] = useState(null);
  const [isLoadingStudent, setIsLoadingStudent] = useState(false);
  const [aiTrackerTab, setAiTrackerTab] = useState('ai');

  // 문제 추출 관련 상태 (추출 결과 표시 UI 는 추후 별도 작업)
  const [extractedProblems, setExtractedProblems] = useState([]);
  const [problemsByTree, setProblemsByTree] = useState({});
  const [isExtracting, setIsExtracting] = useState(false);
  const [engineData, setEngineData] = useState(null);
  const [geneticsData, setGeneticsData] = useState(null);

  // 모달 상태
  const [showExtractionConfig, setShowExtractionConfig] = useState(false);

  // AITracker 수학개념 다중 선택 → 문제 미리보기 필터(선택 개념을 모두 가진 문제, AND). [{cid,label}]
  const [selectedConcepts, setSelectedConcepts] = useState([]);
  // 강의 행 보기 — 강의 행을 누르면 오른쪽 목록이 그 행의 검색 조건 그대로 거른다(설계 FE-20260914 §8 D10). { key, name, query }.
  // 강의 행이 아닌 손(개념 표·차트·칩)으로 선택이 바뀌면 풀린다 — 더는 그 행의 조건이 아니므로
  const [syllabusRow, setSyllabusRow] = useState(null);
  const handleConceptSelect = useCallback((cid, name) => {
    const label = typeof name === 'string' ? name.replace(/<[^>]*>/g, '').trim() : '';
    setSyllabusRow(null);
    setSelectedConcepts(prev =>
      prev.some(c => c.cid === cid)
        ? prev.filter(c => c.cid !== cid)   // 재클릭 → 해제
        : [...prev, { cid, label }]          // 새 개념 → 추가
    );
  }, []);
  // 좌표평면 드래그 다중 선택 → 배치 add-only 병합(중복 제거, setState 1회). 토글 아님.
  const handleAddConcepts = useCallback((batch) => {
    if (!Array.isArray(batch) || batch.length === 0) return;
    setSyllabusRow(null);
    setSelectedConcepts(prev => {
      const have = new Set(prev.map(c => c.cid));
      const add = batch
        .filter(c => c && c.cid != null && !have.has(c.cid))
        .map(c => ({ cid: c.cid, label: typeof c.name === 'string' ? c.name.replace(/<[^>]*>/g, '').trim() : '' }));
      return add.length ? [...prev, ...add] : prev;
    });
  }, []);
  const handleRemoveConcept = useCallback((cid) => {
    setSelectedConcepts(prev => prev.filter(c => c.cid !== cid));
    setSyllabusRow(null);
  }, []);
  const handleClearConcepts = useCallback(() => {
    setSelectedConcepts([]);
    setSyllabusRow(null);
  }, []);
  // ConceptTableRow(React.memo) 하이라이트용 — 안정 참조로 메모 유지
  const selectedConceptCids = useMemo(() => selectedConcepts.map(c => c.cid), [selectedConcepts]);

  // 차트 아래 칸 보기 · 강의 행 누름 — 강의 행은 선택 개념을 그 행의 개념으로 **교체**한다(추가·토글인 위 두 손과 다르다).
  // 어느 행을 쓰는지(syllabusRow)는 여기 두어, 보기를 오가며 강의 보기가 내려갔다 올라와도 고른 면이 이어진다
  const [tableView, setTableView] = useState(readTableView);
  const changeTableView = useCallback((v) => {
    setTableView(v);
    try { window.localStorage.setItem(TABLE_VIEW_KEY, v); } catch (e) { /* 곁들이는 기억 */ }
  }, []);
  // 강의 행 누름 — 선택 개념을 그 행의 개념으로 바꾸고(차트 강조) 오른쪽 목록을 그 행의 검색 조건으로. null 이면 둘 다 해제
  const handlePickRow = useCallback((pick) => {
    setSelectedConcepts(pick && Array.isArray(pick.concepts) ? pick.concepts : []);
    setSyllabusRow(pick ? { key: pick.key, name: pick.name, query: pick.query } : null);
  }, []);

  // 문제 추출 (get-pids + tree-info)
  const handleExtractProblems = useCallback(async ({ difficulty, userSelectedCids } = {}) => {
    if (!engineData || !studentId) {
      alert('AI Tracker 데이터를 먼저 로드해주세요.');
      return;
    }
    setIsExtracting(true);
    try {
      const token = await getAccessToken();

      // 1. get-pids API 호출
      const getPidsResponse = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/genetics/get-pids`,
        {
          method: 'POST',
          headers: { 'Authorization': token, 'Content-Type': 'application/json' },
          body: JSON.stringify({
            concept_index: engineData.concept_index,
            r_status: engineData.r_status,
            h_status: engineData.h_status,
            stability: engineData.stability,
            total_record: engineData.total_record,
            v_scale: engineData.v_scale,
            user_selected_cids: userSelectedCids || [],
            difficulty: difficulty || [1, 2, 3, 4, 5],
            problem_number: 200,
            solved_exception: true,
            student_id: parseInt(studentId)
          })
        }
      );
      if (!getPidsResponse.ok) {
        throw new Error('문제 추출 API 실패');
      }
      const getPidsResult = await getPidsResponse.json();
      const problems = getPidsResult.data?.problems || [];
      if (problems.length === 0) {
        alert('추출된 문제가 없습니다.');
        setExtractedProblems([]);
        setProblemsByTree({});
        return;
      }

      // 2. referencefields/by-pids API 호출 (200개씩 분할)
      const BATCH_SIZE = 200;
      const problemIds = problems.map(p => p.problem_id);
      const batches = [];
      for (let i = 0; i < problemIds.length; i += BATCH_SIZE) {
        batches.push(problemIds.slice(i, i + BATCH_SIZE));
      }
      const treeInfoResults = await Promise.all(
        batches.map(async (batch) => {
          const response = await fetch(
            `${process.env.REACT_APP_API_BASE_URL}/api/referencefields/by-pids?problem_ids=${batch.join(',')}`,
            { headers: { 'Authorization': token, 'Content-Type': 'application/json' } }
          );
          if (!response.ok) {
            console.error('tree-info API 실패:', response.status);
            return [];
          }
          const result = await response.json();
          return result.data || [];
        })
      );
      const treeInfoList = treeInfoResults.flat();

      // 3. 데이터 병합 (한 문제가 여러 tree_id를 가질 수 있음)
      const treeInfoByPid = {};
      treeInfoList.forEach(item => {
        if (!treeInfoByPid[item.problem_id]) {
          treeInfoByPid[item.problem_id] = [];
        }
        treeInfoByPid[item.problem_id].push(item);
      });
      const mergedProblems = problems.map(p => ({
        ...p,
        treeInfos: treeInfoByPid[p.problem_id] || [],
      }));
      setExtractedProblems(mergedProblems);

      // 4. tree_id 기준 그룹화 (한 문제가 여러 tree에 속할 수 있음)
      const grouped = {};
      mergedProblems.forEach(p => {
        p.treeInfos.forEach(info => {
          if (info.tree_id !== null) {
            if (!grouped[info.tree_id]) {
              grouped[info.tree_id] = [];
            }
            grouped[info.tree_id].push({
              ...p,
              tree_id: info.tree_id,
              address: info.address || null,
              step: info.step || null,
            });
          }
        });
      });
      setProblemsByTree(grouped);
    } catch (error) {
      console.error('문제 추출 실패:', error);
      alert('문제 추출에 실패했습니다.');
    } finally {
      setIsExtracting(false);
    }
  }, [engineData, studentId, getAccessToken]);

  // 학생 정보 조회
  const fetchStudentInfo = useCallback(async () => {
    if (!studentId) return;
    try {
      setIsLoadingStudent(true);
      const token = await getAccessToken();
      if (!token) {
        navigate('/signin');
        return;
      }
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/users/student/card?student_id=${studentId}`,
        { headers: { 'Authorization': token, 'Content-Type': 'application/json' } }
      );
      if (response.status === 404) {
        console.error(`학생 ID ${studentId}를 찾을 수 없습니다.`);
        if (isPage) navigate(-1);
        return;
      }
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const result = await response.json();
      if (result.status === 'success' && result.data) {
        const d = result.data;
        setStudent({
          student_id: d.student_id,
          student_name: d.student_name,
          school_name: d.school_name,
          school_grade: d.student_grade || d.school_grade,
        });
      }
    } catch (error) {
      console.error('학생 정보 조회 실패:', error);
    } finally {
      setIsLoadingStudent(false);
    }
  }, [studentId, getAccessToken, navigate, isPage]);

  // 초기 데이터 로드
  useEffect(() => {
    fetchStudentInfo();
  }, [fetchStudentInfo]);

  // 강의 보기가 받아 둔 배지 집계·푼 문제는 이 학생 화면이 떠 있는 동안만 — 닫히면(다른 학생·다른 메뉴·로그아웃) 비운다
  useEffect(() => () => clearHubSyllabusCaches(), []);

  // AITrackerContent 콜백
  const handleEngineDataLoad = useCallback((data) => setEngineData(data), []);
  const handleGeneticsDataLoad = useCallback((data) => setGeneticsData(data), []);

  const renderExtractionButton = (isMobileView) => (
    <Button
      variant="primary"
      size="sm"
      onClick={() => setShowExtractionConfig(true)}
      disabled={isExtracting || !engineData}
      className="flex shrink-0 items-center justify-center gap-2"
      style={{ width: isMobileView ? undefined : isPage ? '130px' : '90px' }}
      aria-label="AI 분석"
    >
      {isExtracting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
      {!isMobileView && 'AI 분석'}
    </Button>
  );

  const renderInner = (isMobileView) => (
    <div
      className={`flex flex-col overflow-hidden rounded-3xl bg-white shadow-lg ${isMobileView ? 'flex-1' : 'w-full'}`}
      style={
        isMobileView
          ? (isPage ? { width: 'calc(100% - 28.8px)', height: 'calc(100% - 28.8px)', margin: '14.4px' } : { width: '100%' })
          : { height: '100%' }
      }
    >
      {/* 페이지 헤더 */}
      <div className="shrink-0 border-b">
        <div className={`w-full ${isMobileView ? 'px-3 py-2' : 'px-4 py-3'}`}>
          <div className="flex items-center justify-between gap-3">
            {isPage ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/student')}
                className="flex shrink-0 items-center justify-center gap-1"
                style={{ width: isMobileView ? undefined : '130px' }}
              >
                <ArrowLeft className="w-4 h-4" />
                {!isMobileView && '학생관리'}
              </Button>
            ) : (
              renderExtractionButton(isMobileView)
            )}

            {/* 학생 정보 카드 - 중앙 */}
            <div className="flex min-w-0 flex-1 items-center gap-3 rounded-3xl border border-gray-200 bg-white px-3 py-1.5 shadow-sm">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary text-sm text-white font-bold">
                {student?.student_id || parseInt(studentId)}
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold text-gray-900">{student?.student_name || '학생'}</p>
                <p className="truncate text-xs text-gray-500">
                  {student?.school_name} {student?.school_grade ? `${student.school_grade}학년` : ''}
                </p>
              </div>
              {geneticsData && (
                <div className="flex shrink-0 gap-3 text-sm">
                  {engineData && (
                    <div className="text-center" title="학생이 응시하여 채점된 테스트(시험) 수">
                      <p className="text-[10px] text-gray-500">테스트 수</p>
                      <p className="text-xs font-bold text-gray-900">{engineData.exam_counts}</p>
                    </div>
                  )}
                  <div className="text-center" title="학생이 푼 전체 문항 수 (같은 문제를 여러 번 풀면 중복 포함)">
                    <p className="text-[10px] text-gray-500">총 풀이</p>
                    <p className="text-xs font-bold text-primary">{geneticsData.solved_problems}</p>
                  </div>
                  <div className="text-center" title="중복을 제외한 서로 다른(고유) 문제 수">
                    <p className="text-[10px] text-gray-500">고유 문제</p>
                    <p className="text-xs font-bold text-green-600">{geneticsData.total_problems}</p>
                  </div>
                </div>
              )}
            </div>

            {isPage ? renderExtractionButton(isMobileView) : (
              <Button
                variant="ghost"
                size="sm"
                onClick={onClose}
                className="flex shrink-0 items-center justify-center gap-1"
                style={{ width: isMobileView ? undefined : '90px' }}
                aria-label="닫기"
              >
                <X className="w-4 h-4" />
                {!isMobileView && '닫기'}
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* 본문: 데스크톱 = [AI Tracker | 문제 미리보기(세로)] 2열 / 모바일 = 세로 스택 */}
      <section className={`flex-1 min-h-0 ${isMobileView ? 'overflow-auto' : ''}`}>
        <div className={`flex w-full gap-4 ${isMobileView ? 'flex-col px-3 py-4' : 'h-full flex-row px-4 py-4'}`}>
          {/* 좌: AI Tracker (차트 위 / 테이블 아래) */}
          <div className={isMobileView ? 'h-[440px] w-full shrink-0' : 'flex min-h-0 min-w-[300px] flex-[3]'}>
            <div className="flex h-full w-full min-h-0 flex-col">
              <AITrackerContent
                studentId={parseInt(studentId)}
                studentInfo={student || { student_name: '학생', school_name: '', school_grade: '' }}
                activeTab={aiTrackerTab}
                onTabChange={setAiTrackerTab}
                onEngineDataLoad={handleEngineDataLoad}
                onGeneticsDataLoad={handleGeneticsDataLoad}
                hideStudentInfo
                stackChartTable
                hideEngineSummary
                onConceptSelect={handleConceptSelect}
                onConceptsSelect={handleAddConcepts}
                selectedConceptCids={selectedConceptCids}
                /* 추이 → 시점 클릭으로 열리는 문제 미리보기를 v2 로 세운다.
                   AITrackerContent 는 대상 밖 KyTestPage·StudentManagement 도 쓰는 공유 부품이라
                   **여기서만 켜서** 그 두 화면은 구형 그대로 둔다
                   (같은 파일의 hideEngineSummary 가 쓰는 방식 — KyTestPage:366 이 그 선례다). */
                useV2Preview
                /* 추이 날짜의 구 시험 결과에 마감된 신 시험지(v2) 배정을 합친다.
                   미리보기 구현 선택과 데이터 원천 선택은 별도 플래그로 두어 공유 화면에 번지지 않게 한다. */
                includeV2Assignments
                /* 기간 설정 칸을 줄인다 — 날짜는 입력칸 한 곳에만, 슬라이더 위아래 날짜 글자 줄을 뺀다(계산 약 104px → 69px).
                   이것도 여기서만 켠다 */
                compactDateRange
                /* 차트 아래 칸 [매스컨셉 | 강의] 전환 — 이 두 자리도 여기서만 넘긴다(설계 FE-20260914 §7).
                   매스컨셉이면 표 위 머리 띠에 전환 단추만, 강의면 머리 띠·표 대신 강의 보기(자기 머리 띠 같은 자리에 같은 단추) */
                stackedTableHeader={tableView === 'concept' ? (
                  <div className="flex shrink-0 items-center gap-2 rounded border border-gray-200 bg-gray-50 px-2 py-1">
                    <TableViewToggle value={tableView} onChange={changeTableView} />
                  </div>
                ) : null}
                stackedTableAlt={tableView === 'syllabus' ? (
                  <HubSyllabusBadgeList
                    studentId={parseInt(studentId, 10)}
                    headerLead={<TableViewToggle value={tableView} onChange={changeTableView} />}
                    activeRowKey={syllabusRow?.key ?? null}
                    onPickRow={handlePickRow}
                  />
                ) : null}
              />
            </div>
          </div>
          {/* 우: 문제 미리보기 + 테스트 리스트 (세로 배치, 학습분석 컴포넌트 재사용) */}
          <div className={isMobileView ? 'w-full shrink-0' : 'min-h-0 min-w-[300px] flex-[2]'}>
            <ProblemPreviewPanel
              studentId={studentId}
              student={student}
              vertical
              fillHeight={!isMobileView}
              selectedConcepts={selectedConcepts}
              onRemoveConcept={handleRemoveConcept}
              onClearConcepts={handleClearConcepts}
              syllabusRow={syllabusRow}
              onLearningChanged={onLearningChanged}
            />
          </div>
        </div>
      </section>

      {/* AI 문제 추출 설정 모달 */}
      {showExtractionConfig && (
        <ExtractionConfigModal
          isOpen={showExtractionConfig}
          onClose={() => setShowExtractionConfig(false)}
          onExtract={async ({ difficulty, userSelectedCids }) => {
            setShowExtractionConfig(false);
            await handleExtractProblems({ difficulty, userSelectedCids });
          }}
          isExtracting={isExtracting}
          engineData={engineData}
        />
      )}
    </div>
  );

  if (isPage) {
    return (
      <BypassLayout
        loading={isLoadingStudent}
        renderContent={({ isMobileView }) => renderInner(isMobileView)}
      />
    );
  }

  // embedded: hub 셸 안에서 bare 렌더
  return renderInner(hookIsMobileView);
}

export default StudentLearningPanel;
